import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import ShieldIcon from '@mui/icons-material/Shield';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const PRIMARY_COLOR = '#3d475d';

const steps = ['Correo', 'Código', 'Nueva Contraseña'];
const stepIcons = [<EmailIcon fontSize="large" />, <ShieldIcon fontSize="large" />, <LockIcon fontSize="large" />];

const RecuperarContrasenaPage = ({ onBackToLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [step, setStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [realOTP, setRealOTP] = useState('');

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const timerRef = useRef(null);

  const [errors, setErrors] = useState({});

  const validateEmail = (val) => /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/.test(val);

  const handleSendCode = async () => {
    setErrors({});
    if (!email || !validateEmail(email)) {
      setErrors({ email: 'Correo inválido' });
      return;
    }
    setIsLoading(true);
    const { data } = await supabase.from('person').select('*').eq('email', email.toLowerCase()).maybeSingle();
    if (!data) {
      setSnackbar({ open: true, message: 'Correo no registrado', severity: 'error' });
      setIsLoading(false);
      return;
    }
    const generatedOTP = (100000 + Math.floor(Math.random() * 899999)).toString();
    setRealOTP(generatedOTP);
    await supabase.functions.invoke('email-otp', {
      body: { email: email.toLowerCase(), otp_code: generatedOTP },
    });
    setStep(1);
    setResendSeconds(30);
    timerRef.current = setInterval(() => setResendSeconds(s => s - 1), 1000);
    setIsLoading(false);
  };

  const handleCheckOTP = () => {
    if (otpCode === realOTP) setStep(2);
    else setSnackbar({ open: true, message: 'Código incorrecto', severity: 'error' });
  };

  const handlePasswordChange = async () => {
    const errs = {};
    if (password.length < 6) errs.password = 'Mínimo 6 caracteres';
    if (!password) errs.password = 'Por favor ingresa una contraseña';
    if (!confirmPassword) errs.confirmPassword = 'Confirma tu contraseña';
    if (password !== confirmPassword) errs.confirmPassword = 'Las contraseñas no coinciden';
    if (Object.keys(errs).length) return setErrors(errs);

    setIsLoading(true);
    const { error } = await supabase.functions.invoke('-admin-password-update', {
      body: { email: email.toLowerCase(), password },
    });
    setIsLoading(false);
    if (error) return setSnackbar({ open: true, message: error.message, severity: 'error' });
    setSnackbar({ open: true, message: '¡Contraseña actualizada!', severity: 'success' });
    navigate('/login');
  };

  useEffect(() => {
    if (resendSeconds <= 0 && timerRef.current) clearInterval(timerRef.current);
  }, [resendSeconds]);

  return (
    <Container maxWidth="sm">
      <Box mt={6}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 4 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton onClick={() => navigate('/login')}><ArrowBackIcon /></IconButton>
            <Typography variant="body1">Volver</Typography>
          </Box>

          <Typography variant="h5" align="center" fontWeight={700} color={PRIMARY_COLOR} mb={3}>
            Recuperar Contraseña
          </Typography>

          <Stepper activeStep={step} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel StepIconComponent={() => (
                  step > index ? <CheckCircleIcon color="success" /> : step === index ? stepIcons[index] : <div style={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#ccc' }} />
                )}>{index + 1}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {step === 0 && (
            <Box mt={4} textAlign="center">
              <Box mb={2}>{stepIcons[0]}</Box>
              <Typography gutterBottom>
                Ingresa tu correo electrónico y te enviaremos un código de verificación.
              </Typography>
              <TextField
                label="Correo electrónico"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                sx={{ mt: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 3, backgroundColor: PRIMARY_COLOR, ':hover': { backgroundColor: '#2e3548' } }}
                onClick={handleSendCode}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Enviar código'}
              </Button>
            </Box>
          )}

          {step === 1 && (
            <Box mt={4} textAlign="center">
              <Box mb={2}>{stepIcons[1]}</Box>
              <Typography mb={2}>
                Ingresa el código de 6 dígitos que enviamos a:<br /><strong>{email}</strong>
              </Typography>
              <Box display="flex" justifyContent="center" gap={1}>
                {[...Array(6)].map((_, i) => (
                  <TextField
                    key={i}
                    value={otpCode[i] || ''}
                    onChange={(e) => {
                      const chars = otpCode.split('');
                      chars[i] = e.target.value;
                      setOtpCode(chars.join(''));
                    }}
                    inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                    sx={{ width: 48 }}
                  />
                ))}
              </Box>
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 3, backgroundColor: PRIMARY_COLOR, ':hover': { backgroundColor: '#2e3548' } }}
                onClick={handleCheckOTP}
                disabled={otpCode.length !== 6}
              >
                Verificar código
              </Button>
              {resendSeconds > 0 ? (
                <Typography align="center" mt={2}>
                  Podrás reenviar en {resendSeconds} segundos
                </Typography>
              ) : null}
            </Box>
          )}

          {step === 2 && (
            <Box mt={4} textAlign="center">
              <Box mb={2}>{stepIcons[2]}</Box>
              <Typography mb={2}>Ingresa tu nueva contraseña</Typography>
              <TextField
                fullWidth
                label="Nueva contraseña"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Confirmar contraseña"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
              />
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 3, backgroundColor: PRIMARY_COLOR, ':hover': { backgroundColor: '#2e3548' } }}
                onClick={handlePasswordChange}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Actualizar Contraseña'}
              </Button>
            </Box>
          )}
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default RecuperarContrasenaPage;
